// src/pages/Dashboard.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { logger } from "../utils/logger";
import { logout } from "../redux/authSlice";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DonorManagement from "./DonorManagement";
import DonationManagement from "./DonationManagment";
import ImportDataPage from "./ImportData";
import UserManagement from "../components/UserManagement";
import Report from "./Report";
import ResetPrasadPage from "./ResetPrasadPage";
import UserList from "./UserList";
import ManageOptions from "./ManageOptions";
import UserLogDashboard from "./UserLogsManagement";
import DataExportPage from "./DataExportPage";
import PayPurposePage from "./PayPurposePage";
import ConfirmationDialog from "../components/ConfirmationDialog";
import { usePageLogger } from "../hooks/usePageLogger";

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
const [filterData, setFilterData] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState("dashboard");
  const [openUserPopup, setOpenUserPopup] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false); // new state
  usePageLogger("Dashboard Page", {
    username: user?.username,
    role: user?.role,
  });

  // const handleMenuSelect = useCallback((menu) => {
  //   setSelectedMenu(menu);
  // }, []);

  const handleMenuSelect = useCallback((menu, data = null) => {
  setSelectedMenu(menu);
  setFilterData(data); // store filter info if provided
}, []);

  const confirmLogout = useCallback(() => {
    setLogoutDialogOpen(false);

    logger.info("Logout confirmed", {
      page: "Dashboard",
      username: user?.username,
      role: user?.role,
    });

    try {
      dispatch(logout());
      logger.info("User logged out successfully", {
        page: "Dashboard",
        username: user?.username,
        role: user?.role,
      });
      navigate("/login");
    } catch (error) {
      logger.error("Error during logout", {
        page: "Dashboard",
        error: error,
        username: user?.username,
        role: user?.role,
      });
    }
  }, [dispatch, navigate, user]);

  const handleLogout = useCallback(() => {
    setLogoutDialogOpen(true);
    logger.info("Logout confirmation dialog opened", {
      page: "Dashboard",
      username: user?.username,
      role: user?.role,
    });
  }, [user]);

  const handleCancelLogout = useCallback(() => {
    setLogoutDialogOpen(false);
    logger.info("Logout confirmation dialog closed (cancelled)", {
      page: "Dashboard",
      username: user?.username,
      role: user?.role,
    });
  }, [user]);

  const handleAddUser = useCallback(() => {
    setOpenUserPopup(true);
    logger.info("Add User modal opened", {
      page: "Dashboard",
      username: user?.username,
      role: user?.role,
    });
  }, []);

  const handleCloseUserPopup = useCallback(() => {
    setOpenUserPopup(false);
    logger.info("Add User modal closed", {
      page: "Dashboard",
      username: user?.username,
      role: user?.role,
    });
  }, [user]);

  useEffect(() => {
    if (location.state?.selectedMenu) {
      setSelectedMenu(location.state.selectedMenu);
      logger.info("Selected menu restored from location state", {
        page: "Dashboard",
        menu: location.state.selectedMenu,
        username: user?.username,
        role: user?.role,
      });
    } else {
      setSelectedMenu("dashboard");
      logger.info("Default menu set to dashboard", {
        page: "Dashboard",
        username: user?.username,
        role: user?.role,
      });
    }
  }, [location.state, user]);

  useEffect(() => {
    if (!isAuthenticated) {
      logger.info("User not authenticated, redirecting to login", {
        page: "Dashboard",
        username: user?.username,
        role: user?.role,
      });
      navigate("/login");
    }
  }, [isAuthenticated, navigate, user]);

  const renderContent = useMemo(() => {
    switch (selectedMenu) {
      case "dashboard":
        return <Typography variant="h5">Welcome to the Dashboard!</Typography>;
      case "donors":
        return <DonorManagement />;
      case "donations":
        return <DonationManagement filterData={filterData} />;
      case "paypurpose-details": 
        return <PayPurposePage onMenuSelect={handleMenuSelect} />;
      case "reports":
        return <Report />;
      case "import":
        return <ImportDataPage />;
      case "reset-prasad":
        return <ResetPrasadPage />;
      case "user-list":
        return <UserList />;
      case "manage-options":
        return <ManageOptions />;
      case "user-logs":
        return <UserLogDashboard />;
      case "data-export":
        return <DataExportPage />;
      default:
        return (
          <Typography variant="h5">
            Select a section to view details.
          </Typography>
        );
    }
  }, [selectedMenu, filterData, handleMenuSelect]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>

      <Header user={user} onLogout={handleLogout} />
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>

        <Sidebar onMenuSelect={handleMenuSelect} user={user} />
        <Box
          sx={{
            flexGrow: 1,
            padding: "24px",
            backgroundColor: "#f5f5f5",
            opacity: 0.95,
            display: "flex",
            flexDirection: "column",
          }}
        >

          {renderContent}

          {selectedMenu === "dashboard" &&
            (user?.role === "superadmin" || user?.role === "admin1" || user?.role === "admin2") && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mb: 2,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddUser}
                >
                  Add User
                </Button>
              </Box>
            )}
        </Box>

        {openUserPopup && (
          <UserManagement open={openUserPopup} onClose={handleCloseUserPopup} />
        )}
      </Box>
      <Footer />
      <ConfirmationDialog
        open={logoutDialogOpen}
        title="Confirm Logout"
        message="Are you sure you want to logout?"
        onConfirm={confirmLogout}
        onCancel={handleCancelLogout}
      />
    </Box>
  );
};

export default Dashboard;
